import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, eur } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, X, Eye, ArrowLeft } from "lucide-react";

const norm = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

export default function PreviewMenu() {
  const [menu, setMenu] = useState(null);
  const [search, setSearch] = useState("");
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    // Fase 10: preview reads from LIVE (draft) state so admins see what will
    // become the customer menu at the next publish. Falls back to /public/menu
    // if unauthorized (e.g. accessed while logged out).
    api.get("/admin/menu/preview")
      .then((r) => setMenu(r.data))
      .catch(() => api.get("/public/menu").then((r) => setMenu(r.data)).catch(() => {}));
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const productsByCat = useMemo(() => {
    const map = {};
    (menu?.products || []).forEach((p) => { (map[p.category_id] = map[p.category_id] || []).push(p); });
    return map;
  }, [menu]);

  const catById = useMemo(() => {
    const map = {};
    (menu?.categories || []).forEach((c) => { map[c.id] = c; });
    return map;
  }, [menu]);

  const searchResults = useMemo(() => {
    const q = norm(search);
    if (!q || !menu) return null;
    const terms = q.split(/\s+/).filter(Boolean);
    return menu.products.filter((p) => {
      const hay = norm([p.name, p.description, (p.tags || []).join(" "), (p.allergens || []).join(" "), catById[p.category_id]?.name || ""].join(" "));
      return terms.every((t) => hay.includes(t));
    });
  }, [search, menu, catById]);

  if (!menu) return <div className="p-8 text-ink-500">Caricamento anteprima…</div>;

  return (
    <div className="min-h-screen pb-16 bg-sand-50" data-testid="preview-menu-page">
      {/* Preview banner */}
      <div className="sticky top-0 z-30 bg-bronze-600 text-white px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <Eye className="w-4 h-4" />
          <b>ANTEPRIMA MENU CLIENTE</b>
          <span className="opacity-80 hidden sm:inline">— vedi il menu come lo vedono i clienti adesso ({now.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })})</span>
        </div>
        <Link to="/admin/menu" className="text-xs bg-white/20 hover:bg-white/30 rounded-full px-3 py-1 flex items-center gap-1" data-testid="preview-back">
          <ArrowLeft className="w-3 h-3" />Torna all'admin
        </Link>
      </div>

      <header className="bg-sand-100/95 backdrop-blur-xl border-b border-sand-300 px-5 py-4">
        <div className="eyebrow">Sarabi Beach</div>
        <div className="font-serif text-2xl leading-none">Menu</div>
        <div className="text-xs text-ink-500 mt-1">Modalità sola lettura — nessun ordine possibile</div>

        <div className="mt-3 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none" />
          <Input
            data-testid="preview-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca nel menu…"
            className="pl-9 pr-9 rounded-full bg-white border-sand-300"
          />
          {search && (
            <button data-testid="preview-search-clear" onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-sand-200 flex items-center justify-center text-ink-500">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {menu.menus_used && menu.menus_used.length > 0 && (
          <div className="mt-2 text-[11px] text-ink-500">
            Menu attivi: <b>{menu.menus_used.join(", ")}</b>
          </div>
        )}
      </header>

      <main className="px-5 mt-4 space-y-8">
        {searchResults !== null ? (
          <section data-testid="preview-search-results">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-3xl">Risultati</h2>
              <span className="text-sm text-ink-500">{searchResults.length} {searchResults.length === 1 ? "prodotto" : "prodotti"}</span>
            </div>
            {searchResults.length === 0 ? (
              <div className="card-elevated p-6 text-center text-ink-500">Nessun prodotto per "{search}"</div>
            ) : (
              <div className="grid gap-3">
                {searchResults.map((p) => <ProductCard key={p.id} p={p} category={catById[p.category_id]?.name} />)}
              </div>
            )}
          </section>
        ) : menu.categories.length === 0 ? (
          <div className="card-elevated p-8 text-center">
            <div className="font-serif text-xl">Nessuna categoria visibile in questo momento</div>
            <div className="text-sm text-ink-500 mt-1">Le fasce orarie stanno filtrando tutte le categorie. Prova a rilassare le fasce o cambiare orario.</div>
          </div>
        ) : (
          menu.categories.map((c) => (
            <section key={c.id} data-testid={`preview-cat-${c.id}`}>
              <h2 className="text-3xl mb-3">{c.name}</h2>
              <div className="grid gap-3">
                {(productsByCat[c.id] || []).map((p) => <ProductCard key={p.id} p={p} />)}
                {(productsByCat[c.id] || []).length === 0 && <div className="text-ink-500 text-sm">Nessun prodotto disponibile in questa categoria</div>}
              </div>
            </section>
          ))
        )}
      </main>
    </div>
  );
}

function ProductCard({ p, category }) {
  return (
    <div className="card-elevated p-4 flex justify-between gap-4" data-testid={`preview-product-${p.id}`}>
      <div className="flex-1">
        {category && <div className="eyebrow text-bronze-600">{category}</div>}
        <div className="font-medium mt-1">{p.name}</div>
        {p.description && <div className="text-sm text-ink-500 mt-1">{p.description}</div>}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(p.tags || []).map((t) => <span key={t} className="chip">{t}</span>)}
          {(p.allergens || []).length > 0 && <span className="chip text-bronze-600 border-bronze-500/40">allergeni: {p.allergens.join(", ")}</span>}
        </div>
      </div>
      <div className="text-right">
        <div className="font-serif text-lg">{eur(p.price_cents)}</div>
      </div>
    </div>
  );
}
